import { useEffect, useState } from "react";

import { useLocation, useNavigate, useParams } from "react-router-dom";

import { ExclamationCircleIcon } from "@heroicons/react/24/outline";

import { ILead } from "../../models/Lead";
import Avatar from "../../components/user/Avatar";
import LeadSMS from "../../components/leads/LeadSMS";
import useSingleLead from "../../data/useSingleLead";
import useSav from "../../data/useSav";
import LeadTab from "../../components/leads/LeadTab";
import { useAuth } from "../../contexts/AuthContext";
import Tabs from "../../components/shared/Tabs/Tabs";
import useLeadMessages from "../../data/useLeadMessages";
import useVehicleLeads from "../../data/useVehicleLeads";
import { useSignalR } from "../../contexts/SignalRContext";
import useLeadDocuments from "../../data/useLeadDocuments";
import LoadingWheel from "../../components/shared/LoadingWheel";
import LeadDocuments from "../../components/leads/LeadDocuments";
import LeadParts from "../../components/leads/LeadParts/LeadParts";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import LeadNotes from "../../components/leads/LeadInformation/LeadNotes";
import Breadcrumbs from "../../components/shared/Breadcrumbs/Breadcrumbs";
import useNotification from "../../components/notifications/useNotifications";
import VehicleNotes from "../../components/leads/LeadInformation/VehicleNotes";
import PartVehicleDetails from "../../components/leads/LeadParts/PartVehicleDetails";
import WarningBar from "../../components/shared/Card/WarningBar";
import EbayReturnsTable from "../../components/ebayReturns/EbayReturnsTable";
import openInNewTab from "../../utils/openInNewTab";
import QuoteWorkflowCard from "../../components/leads/LeadInformation/QuoteWorkflowCard";
import { wilmaSAVCustomerId } from "../../config";
import LeadActions from "../../components/leads/LeadActions";
import LeadStatusCard from "../../components/leads/LeadInformation/LeadStatusCard";
import LeadWorkflowCard from "../../components/leads/LeadInformation/LeadWorkflowCard";


const hexToRgba = (hexColor: string, alpha: number) => {
  const hex = hexColor.replace("#", "");
  const normalizedHex = hex.length === 3
    ? hex.split("").map((c) => c + c).join("")
    : hex;
  if (normalizedHex.length !== 6) return `rgba(16, 185, 129, ${alpha})`;

  const red = parseInt(normalizedHex.substring(0, 2), 16);
  const green = parseInt(normalizedHex.substring(2, 4), 16);
  const blue = parseInt(normalizedHex.substring(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

export default function Lead({ modalLeadId, setOpen }: { modalLeadId?: string, setOpen?: (open: boolean) => void }) {
  const { leadId: paramLeadId, tab } = useParams();
  const { pathname } = useLocation();
  const leadId = modalLeadId || paramLeadId;
  const isBookingPath = pathname.startsWith("/bookings");
  const leadLabel = isBookingPath ? "Booking" : "Lead";
  const leadListLabel = isBookingPath ? "Bookings" : "Leads";
  const leadListPath = isBookingPath ? "/bookings" : "/leads";

  const { userId: authUserId, customerId } = useAuth();
  const isSavCustomer = !!customerId && customerId === wilmaSAVCustomerId;
  const { activeLeads, connection } = useSignalR();
  const navigate = useNavigate();
  const { vrmRefresh } = useVehiclesMutations();
  const { addNotification } = useNotification();
  const { saveHandlers } = useDefaultCRUDHandlers("Lead");
  const { setInfo } = usePageNameContext();
  const { leadDocuments } = useLeadDocuments(leadId!, "Lead");
  const { lead: singleLead, remove } = useSingleLead(leadId!, "Lead");
  const { customerNames } = useSav(isSavCustomer ? customerId : undefined, !isSavCustomer, "LeadPage");
  const { leadMessages } = useLeadMessages(singleLead.data?.data?.contactId!, "Lead");
  const { vehicleLeads } = useVehicleLeads(singleLead.data?.data?.vehicleId, "Lead");

  const [lead, setLead] = useState<ILead | undefined>();
  const [extraVehicleLeads, setExtraVehicleLeads] = useState<ILead[] | undefined>();
  const [loading, setLoading] = useState(true);
  const [leadName, setLeadName] = useState<string | undefined>("");
  const [viewType, setViewType] = useState(0);

  const viewTypes = [
    { value: 0, label: "Lead Information" },
    { value: 1, label: `Documents (${leadDocuments.data?.data?.length || 0})` },
    { value: 2, label: `SMS/Email (${leadMessages.data?.data?.length || 0})` },
    { value: 3, label: "Parts" },
    { value: 4, label: "Actions" },
  ];

  const [isDragging, setIsDragging] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);


  const notifyLeadClose = () => {
    if (connection && lead) {
      connection
        .invoke("NotifyLeadClose", lead.id, authUserId)
        .then(() => console.log("Notified hub: Lead Closed"))
        .catch((err: any) => console.error("Error notifying hub (close):", err));
    }
  };

  const handleUpdateVrm = () => {
    //Uppercase and remove whitespace from vrm
    if (!lead?.vehicle?.correctedVrm) return;

    const updatedVehicle = {
      ...lead!.vehicle!,
      vrm: lead.vehicle.correctedVrm.toUpperCase().replace(/\s/g, ""),
    };

    console.log("update", updatedVehicle);

    vrmRefresh.mutate(updatedVehicle, {
      onSuccess: () => {
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      },
    });
  };

  useEffect(() => {
    if (leadName && !modalLeadId) {
      setInfo({
        name: `${leadName}'s ${leadLabel} ${lead?.vehicle?.vrm ? ` - ${lead.vehicle.vrm}` : ""
          }`,
        desc: `View and manage ${leadName}'s ${leadLabel}`,
      });
    }
  }, [leadId, leadName, leadLabel]);

  useEffect(() => {
    if (!singleLead.isLoading) {
      if (singleLead.data?.data) {
        if (tab == "parts") {
          setViewType(3);
        }
        setLead(singleLead.data?.data);
        setLeadName(singleLead.data?.data.contact?.fullname);
        setLoading(false);
      } else {
        addNotification({
          variant: "error",
          primaryText: "Lead not found",
        });
        navigate(-1);
      }
    }
  }, [leadId, singleLead.isLoading, singleLead.data?.data, navigate]);

  useEffect(() => {
    if (vehicleLeads.isLoading) {
      setExtraVehicleLeads(undefined);
      return;
    }

    if (vehicleLeads.data?.data && vehicleLeads.data.data.length > 1) {
      const extras = vehicleLeads.data.data.filter(lead => lead.id !== leadId);
      setExtraVehicleLeads(extras && extras.length > 0 ? extras : undefined);
    } else {
      setExtraVehicleLeads(undefined);
    }
  }, [leadId, vehicleLeads.isLoading, vehicleLeads.data?.data])

  // Notify when the lead is opened, and also set up cleanup handlers.
  useEffect(() => {
    if (connection && lead && authUserId) {
      connection
        .invoke("NotifyLeadOpen", lead.id, authUserId)
        .then(() => console.log("Notified hub: Lead Open"))
        .catch((err: any) => console.error("Error notifying hub (open):", err));
    }

    // Add beforeunload event listener to notify the hub if the window is closing.
    window.addEventListener("beforeunload", notifyLeadClose);

    // Cleanup: notify the hub when this component unmounts (i.e. user closes the lead)
    return () => {
      notifyLeadClose();
      window.removeEventListener("beforeunload", notifyLeadClose);
    };
  }, [connection, lead, authUserId]);

  const ALLOWED_MIME_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "application/pdf",
  ];

  const isDragAllowed = (e: DragEvent) =>
    Array.from(e.dataTransfer?.items || []).some(item => item.kind === "file" && ALLOWED_MIME_TYPES.includes(item.type));

  useEffect(() => {
    const onDragOver = (e: DragEvent) => {
      if (!isDragAllowed(e)) return;
      e.preventDefault();
      // only show the “copy” cursor if there’s at least one allowed file
      e.dataTransfer!.dropEffect = "copy";
    };
    const onDragEnter = (e: DragEvent) => {
      if (!isDragAllowed(e)) return;
      e.preventDefault();
      setIsDragging(true);
    };
    const onDragLeave = (e: DragEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      // only turn off when leaving viewport
      if ((e.target as Element).nodeName === "HTML") {
        setIsDragging(false);
      }
    };
    const onDrop = (e: DragEvent) => {
      if (!isDragging) return;
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer!.files);
      setDroppedFiles(files);
      // switch to “Documents” tab if not already
      if (viewType !== 1) setViewType(1);
    };

    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
    };
  }, [viewType, ALLOWED_MIME_TYPES]);

  const pages = [
    { to: "..", label: leadListLabel, current: false },
    {
      to: leadId && lead ? `${leadListPath}/${leadId}` : "",
      label: leadId && lead ? `${leadLabel} for ${lead.contact?.fullname}` : `New ${leadLabel}`,
      current: true,
    },
  ];
  const allocatedCustomer = customerNames.data?.data?.find(x => x.id === lead?.allocatedToCustomerId);
  const allocatedCustomerName = allocatedCustomer?.name;
  const allocatedCustomerColour = allocatedCustomer?.primaryColour || "#10B981";
  const isAllocatedLead = !!lead?.allocatedToCustomerId;
  const showSavBookingAllocation = isSavCustomer && isBookingPath && isAllocatedLead;

  // When the route's leadId changes (e.g. via the Topbar search) we need a
  // hard unmount/remount of the child subtree so that components which seed
  // their internal state from `lead` / `lead.vehicle` (LeadNotes, VehicleNotes,
  // PartVehicleDetails, workflow cards, etc.) don't keep showing data from the
  // previously viewed lead. Showing the LoadingWheel while our local `lead`
  // state still points at the old lead guarantees the subtree is torn down
  // before rendering the new one.
  const isStaleLead = !!lead && lead.id !== leadId;

  return loading || isStaleLead ? (
    <div className="mt-8 min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow">
      <LoadingWheel />
    </div>
  ) : (
    <div className="space-y-4">
      {isDragging && (
        <div
          className="fixed inset-0 flex items-center justify-center pointer-events-none bg-black bg-opacity-50 z-50"
        >
          <div className="text-white text-2xl">Drop files to upload</div>
        </div>
      )}

      {leadId && activeLeads && activeLeads[leadId] && (
        <div className="ml-2 mt-4 flex items-center space-x-2">
          <span>Currently viewed by</span>
          <div className="flex items-center">
            {activeLeads[leadId].map((viewer, i) => (
              <Avatar
                key={i}
                userId={viewer}
                classes="w-8 h-8 rounded-full [&:not(:first-child)]:-ml-2"
              />
            ))}
          </div>
        </div>
      )}

      {/* Don't want breadcrumbs in the modal version */}
      {!modalLeadId && <Breadcrumbs pages={pages} />}

      {showSavBookingAllocation && (
        <div
          className="rounded-md border p-4 shadow"
          style={{
            borderColor: allocatedCustomerColour,
            backgroundColor: hexToRgba(allocatedCustomerColour, 0.15),
          }}
        >
          <p
            className="font-semibold"
            style={{ color: allocatedCustomerColour }}
          >
            {`Allocated ATF: ${allocatedCustomerName || "Unknown ATF"}`}
          </p>
        </div>
      )}

      {lead && (
        <div className="grid grid-cols-1 lg:grid-cols-4 lg:gap-x-4 gap-y-4">
          <LeadNotes lead={lead!} notesProp="officeNotes" title="Office Notes" />
          <LeadNotes lead={lead!} notesProp="driverNotes" title="Driver Notes" />
          <LeadNotes lead={lead!} notesProp="yardNotes" title="Yard Notes" />
          <VehicleNotes vehicle={lead.vehicle!} />
        </div>
      )}

      <PartVehicleDetails
        vehicle={lead?.vehicle!}
        price={lead ? lead.priceWithVAT : null}
        collectionQuestions
      />


      {lead?.vehicle?.correctedVrm !== null &&
        lead?.vehicle?.correctedVrm.toUpperCase().trim() !==
        lead?.vehicle!.vrm && (
          <WarningBar button={
            <ButtonInput
              label="Update VRM"
              isSubmit={false}
              onClick={handleUpdateVrm}
              classes="bg-red-600 hover:bg-red-700"
            />
          }>
            <div className="flex flex-col space-y-1">
              <h1 className="text-lg">Corrected VRM</h1>
              <p className="text-sm">
                The VRM has been corrected on this vehicle, please confirm
                you'd like to update all details/parts/prices based on the
                new VRM provided.
              </p>
              <p>
                New VRM:{" "}
                <span className="font-bold">
                  {lead?.vehicle?.correctedVrm.toUpperCase()}
                </span>
              </p>
            </div>
          </WarningBar>
        )}

      {extraVehicleLeads && (
        <WarningBar button={
          <div className="flex items-center gap-x-2">
            {extraVehicleLeads.map((lead, i) =>
              <ButtonInput 
                key={i}
                label={`View ${leadLabel}`}
                isSubmit={false}
                onClick={() => openInNewTab(`${leadListPath}/${lead.id}`)}
                classes="bg-red-600 hover:bg-red-700"
              />
            )}
          </div>
        }>
          <div>
            <p>This vehicle has {extraVehicleLeads.length} other lead{extraVehicleLeads.length > 1 && "s"}!</p>
            <p>Please make sure this vehicle has not already been scheduled, collected or paid.</p>
          </div>
        </WarningBar>
      )}

      {lead && (
        isBookingPath 
        ? <QuoteWorkflowCard lead={lead} entityLabel={leadLabel} />
        : <LeadWorkflowCard lead={lead} />
      )}

      <Tabs viewType={viewType} viewTypes={viewTypes} setViewType={setViewType} />

      {
        {
          0: <LeadTab lead={lead!} setOpen={setOpen} isBooking={isBookingPath} />,
          1: <LeadDocuments lead={lead!} droppedFiles={droppedFiles} setDroppedFiles={setDroppedFiles} />,
          2: <LeadSMS lead={lead!} />,
          3: <LeadParts lead={lead!} vehicle={null} />,
          4: <LeadActions lead={lead!} />
        }[viewType]
      }
    </div>
  );
}
